<script lang="ts" setup>
    import { onMounted, ref, nextTick, reactive, computed, watch } from 'vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import ActionButton from "../../components/ActionButton.vue"; 
    import TextInput from '../../components/TextInput.vue'
    import MessageSection from '../../components/Messages/MessageSection.vue'
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import { patient } from '../../lib/ChoiceApi';
    import { IPatientCreateQuickInputs } from '../../lib/Models/PatientModel';
    import { popAlert, confirmDialog } from '../../lib/Notification';

    import { MessageSectionType, DisplayStyle, TextInputType } from "../../Enums/UiEnums";
    import { ActionButtonStyle,billingBaseClass,spinnerClassSmall} from '../../lib/Types/Types';
    import * as Validation from "../../lib/Validation"
    import '../../../css/choice.css';
    //import {open_fax, export_ccd, pdf_print} from "../../../types/LegacyOfficeMDFunctions"

    const emit = defineEmits(["onPatientCreated"]);

    let formData = reactive<{
        patientId?: number,
        firstName: string,
        middleName: string, 
        lastName: string,    
        dateOfBirth: string,
        gender: string,
        phoneHome: string,
        phoneHomeExt: string,
        phoneWork: string,
        phoneWorkExt: string,
        phoneOther: string,
        phoneOtherExt: string,
        sameAsPatient: boolean,
        creating: boolean,
        bypassUnique: boolean,
        actionMessage: string
    }>({
        firstName: "", 
        middleName: "",
        lastName: "", 
        dateOfBirth: "",
        gender: "U",
        phoneHome: "",
        phoneHomeExt: "",
        phoneWork: "",
        phoneWorkExt: "",
        phoneOther: "",
        phoneOtherExt: "",
        sameAsPatient: false,
        creating: false,
        bypassUnique: false,
        actionMessage: ""
    });

    const props = withDefaults(defineProps<{
        saveEnabled: number,
        resetFields?: boolean
    }>(), {
        resetFields: false
    });

    const inputFocus = ref<HTMLInputElement>();   

    onMounted(async () => {
        formData.gender = "U";
        nextTick(() => { inputFocus.value?.focus(); });
    });

    watch(() => props.resetFields, (newValue, oldValue) => {
        if (newValue) {
            ResetFields();
            setTimeout(() => inputFocus.value?.focus(), 500);
        }
    });

    const saveEnabled = computed<boolean>(() => props.saveEnabled == 1 && !formData.creating);

    function ResetFields() {
        formData.patientId = undefined;
        formData.firstName = "";
        formData.middleName = "";
        formData.lastName = "";
        formData.gender = "U";
        formData.dateOfBirth = "";
        formData.phoneHome = "";
        formData.phoneHomeExt = "";
        formData.phoneWork = "";
        formData.phoneWorkExt = "";
        formData.phoneOther = "";
        formData.phoneOtherExt = "";
        formData.sameAsPatient = false;
        formData.creating = false;
        formData.bypassUnique = false;
        formData.actionMessage = "";
    }

    async function CreatePatient() {
        formData.actionMessage = "";

        let errorMessage: string[] = [];
        if (formData.firstName == "")
            errorMessage.push("First Name");
        if (formData.lastName == "")
            errorMessage.push("Last Name");
        if (formData.dateOfBirth == "" || formData.dateOfBirth == undefined)
            errorMessage.push("DOB");
        if (formData.phoneHome == "" && formData.phoneWork == "" && formData.phoneOther == "")
            errorMessage.push("at least one phone number");
        if (errorMessage.length > 0)
        {
            formData.actionMessage = `Required fields: ${errorMessage.join(", ")}`;
            return;            
        }
        const post_data = <IPatientCreateQuickInputs>{
            firstName: formData.firstName,
            middleName: formData.middleName,
            lastName: formData.lastName,
            dateOfBirth: formData.dateOfBirth,
            gender: formData.gender,
            phoneHome: formData.phoneHome,
            phoneHomeExt: formData.phoneHomeExt,
            phoneWork: formData.phoneWork,
            phoneWorkExt: formData.phoneWorkExt,
            phoneOther: formData.phoneOther,
            phoneOtherExt: formData.phoneOtherExt,
            sameAsPatient: formData.sameAsPatient,
            bypassUnique: formData.bypassUnique
        };
        formData.creating = true;
        const response = await patient.createQuick(post_data);
        formData.creating = false;
        if (!response.passed) {
            if (response.duplicate) {
                const result = await confirmDialog(response.message ?? "Patient already exists. Do you wish to continue and create the patient?"); 
                if (result) {
                    formData.bypassUnique = true;
                    CreatePatient();
                    return;
                }
            }
            else {
                formData.actionMessage = response.message ?? "Failed to create the patient.";
            }
            return;
        }

        formData.patientId = response.patientId;
        emit("onPatientCreated", formData.patientId);
    }

</script>

<style>
    .patientCreate-FixedLabelWidth {
        width: 100px;
    }
    .patientCreate-FixedInputWidth {
        width: 250px;
    }
    .patientCreate-FixedRequired {
        width: 5px;
    }
</style>

<template>
    <div class="row">
        <div class="patientCreate-FixedLabelWidth mt-2 label-isalus-small">
            First Name
        </div>
        <div class="patientCreate-FixedRequired mt-1 pe-0 text-danger">*</div>
        <div class="patientCreate-FixedInputWidth">
            <TextInput 
                v-model:text-value="formData.firstName" 
                ref="inputFocus"
                :maxLength="25"
                :title="'First Name. Required Field.'"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
    </div>
    <div class="row mt-2">
        <div class="patientCreate-FixedLabelWidth mt-2 label-isalus-small">
            Middle Name
        </div>
        <div class="patientCreate-FixedRequired mt-1 pe-0 text-danger"></div>
        <div class="patientCreate-FixedInputWidth">
            <TextInput 
                v-model:text-value="formData.middleName" 
                :maxLength="25"
                title="Middle Name"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
    </div>
    <div class="row mt-2 ">
        <div class="patientCreate-FixedLabelWidth mt-2 label-isalus-small">
            Last Name
        </div>
        <div class="patientCreate-FixedRequired mt-1 pe-0 text-danger">*</div>
        <div class="patientCreate-FixedInputWidth">
            <TextInput 
                v-model:text-value="formData.lastName" 
                :maxLength="95"
                title="Last Name. Required Field."
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
    </div>
    <div class="row mt-2">
        <div class="patientCreate-FixedLabelWidth mt-2 label-isalus-small">
            DOB
        </div>
        <div class="patientCreate-FixedRequired mt-1 pe-0 text-danger">*</div>
        <div class="date-isalus-small">
            <DatePickerNoTZ 
                v-model:value="formData.dateOfBirth" 
                :clearable="false"
                :cssFramework="'vanilla'" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="patientCreate-FixedLabelWidth label-isalus-small mt-1">
            Gender
        </div>
        <div class="patientCreate-FixedRequired pe-0 text-danger">*</div>
        <div class="col">
            <div class="form-check form-check-inline">
                <input class="form-check-input" type="radio" name="gender" id="genderFemale" :value="'F'" v-model="formData.gender">
                <label class="form-check-label label-isalus-small" for="gender">Female</label>
            </div>
            <div class="form-check form-check-inline">
                <input class="form-check-input" type="radio" name="gender" id="genderMale" :value="'M'" v-model="formData.gender">
                <label class="form-check-label label-isalus-small" for="gender">Male</label>
            </div>
            <div class="form-check form-check-inline">
                <input class="form-check-input" type="radio" name="gender" id="genderUnkown" :value="'U'" v-model="formData.gender" checked>
                <label class="form-check-label label-isalus-small" for="gender">Unknown</label>
            </div>
        </div>
    </div>
    <div class="row mt-2">
        <div class="patientCreate-FixedLabelWidth mt-2 label-isalus-small">
            Home
        </div>
        <div class="patientCreate-FixedRequired mt-1 pe-0 text-primary">*</div>
        <div class="phone-isalus-small">
            <TextInput 
                v-model:text-value="formData.phoneHome" 
                :maxLength="10"
                :title="'Home Phone Number. One of the phone numbers is required.'"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
        <div class="phone-ext-isalus-small ps-0">
            <TextInput 
                v-model:text-value="formData.phoneHomeExt" 
                :maxLength="6"
                :title="'Home Phone Number Extension'"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
    </div>
    <div class="row mt-2">
        <div class="patientCreate-FixedLabelWidth mt-2 label-isalus-small">
            Work
        </div>
        <div class="patientCreate-FixedRequired mt-1 pe-0 text-primary">*</div>
        <div class="phone-isalus-small">
            <TextInput 
                v-model:text-value="formData.phoneWork" 
                :maxLength="10"
                :title="'Work Phone Number. One of the phone numbers is required.'"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
        <div class="phone-ext-isalus-small ps-0">
            <TextInput 
                v-model:text-value="formData.phoneWorkExt" 
                :maxLength="6"
                :title="'Work Phone Number Extension'"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
    </div>
    <div class="row mt-2">
        <div class="patientCreate-FixedLabelWidth mt-2 label-isalus-small">
            Other
        </div>
        <div class="patientCreate-FixedRequired mt-1 pe-0 text-primary">*</div>
        <div class="phone-isalus-small">
            <TextInput 
                v-model:text-value="formData.phoneOther" 
                :maxLength="10"
                :title="'Other Phone Number. One of the phone numbers is required.'"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
        <div class="phone-ext-isalus-small ps-0">
            <TextInput 
                v-model:text-value="formData.phoneOtherExt" 
                :maxLength="6"
                :title="'Other Phone Number Extension'"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :cssFramework = "'vanilla'">
            </TextInput>
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <input class="form-check-input me-1" type="checkbox" value="" v-model="formData.sameAsPatient"><label class="label-isalus-small">Responsible Party Same as Patient</label>
        </div>
        <div class="col-auto">
            <ActionButton
                baseText = "Create"
                spinText = "Creating"
                :executing="formData.creating"    
                :disabled="formData.creating"   
                :clickEvent="CreatePatient" 
                :toolTip="'Create the patient'"
                :width="125"
                :executeInit="false"
                :base-class="billingBaseClass"
                :overRideStyle="ActionButtonStyle.Save">
            </ActionButton>
        </div>
    </div> 
    <div class="row mt-2">
        <div class="col mt-2 label-isalus-small">
            <MessageSection v-if="formData.actionMessage" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{formData.actionMessage}}
            </MessageSection>    
        </div>
    </div>
</template>

